/**
 * LP-402: what the quote builder screen (src/pages/QuoteEditorPage.jsx) holds while a partner edits a
 * quote with the Copilot docked beside it. Plain functions, no imports: the page and the tests run the
 * same code (imported by the browser bundle).
 *
 * The form: { fields (the header), scope ({ area_sf, dosage_lb_sf, th_sf_per_gal, overage_pct }, or null
 * when the quote has no builder scope), costing (LP-424, the "Cost inputs": tariff, FX, freight model
 * and its inputs, Concria's offer, as modules/q2c/quote-costs.js costingForm gives them; or null), lines
 * (the Quote editor's line shape), off (lines left off the quote, kept so the tools choice can put one back) }.
 *
 * A Copilot change is made and saved by the server. copilotChange() compares the form before it with the
 * quote after it and records every field that moved as ONE change: those fields are tagged "Changed by
 * Copilot", and undo() puts back exactly those fields (the page then saves), but for a change that turned
 * a firm quote budgetary, whose Undo keeps it budgetary (keepsBudgetary, LP-442 r2). A field the partner
 * types into loses its tag (edit()). Nothing here talks to the server.
 *
 * Paths: "fields.<key>", "scope.<key>", "costing.<key>", "line:<id>" (the line itself: added or left off) and
 * "line:<id>.<key>" (one field of a line).
 */

export const LINE_FIELDS = Object.freeze(['type', 'pricing', 'product_id', 'description', 'area_sf', 'dosage_lb_sf', 'waste_pct', 'qty', 'unit', 'unit_price', 'unit_cost', 'optional', 'taxable']);
export const SCOPE_FIELDS = Object.freeze(['area_sf', 'dosage_lb_sf', 'th_sf_per_gal', 'overage_pct']);
// LP-424: the "Cost inputs" fields (the same list as modules/q2c/quote-costs.js COSTING_FIELDS).
export const COSTING_FIELDS = Object.freeze([
  'tariff', 'tariff_pct', 'tariff_source', 'fx_rate', 'fx_date', 'fx_source', 'freight_model',
  'ocean_eur', 'ocean_eur_source', 'trucking_usd', 'trucking_usd_source', 'broker_usd', 'broker_usd_source',
  'pallets_per_container', 'pallets_per_container_source', 'offer_ref', 'offer_date', 'offer_shake_eur_per_kg', 'offer_th_eur_per_l',
]);

const BLANK_LINE = Object.freeze({ type: 'material', pricing: 'per_unit', product_id: '', description: '', qty: '', unit: '', area_sf: '', dosage_lb_sf: '', waste_pct: '', unit_price: '', unit_cost: '', optional: false, taxable: true });

/** A stored line as the form holds it (empty strings for blanks, like the Quote editor). */
export function formLine(l) {
  return { ...BLANK_LINE, ...Object.fromEntries(Object.entries(l || {}).map(([k, v]) => [k, v ?? ''])) };
}

/**
 * The form for a quote. scope: the builder's scope for it (GET /api/quotes/:id/builder), or null;
 * costing: the builder view's costing.form (LP-424), or null.
 */
export function fromQuote(q, { scope = null, costing = null, keys = [] } = {}) {
  return {
    fields: Object.fromEntries(keys.map((k) => [k, q?.[k] ?? ''])),
    scope: scopeForm(scope),
    costing: costingForm(costing),
    lines: (q?.lines || []).map(formLine),
    off: [],
  };
}
const scopeForm = (scope) => (scope ? Object.fromEntries(SCOPE_FIELDS.map((k) => [k, scope[k] ?? ''])) : null);
const costingForm = (c) => (c ? Object.fromEntries(COSTING_FIELDS.map((k) => [k, c[k] ?? ''])) : null);

/**
 * The form after a (re)load. view: the builder view, or null when it failed to load. Then the form has
 * no scope: a save sends none, so the server keeps the quote's own scope (never an older one the form
 * still held), until reseedScope() takes the scope of the next saved view.
 */
export function loadedForm(q, view, { keys = [] } = {}) {
  return fromQuote(q, { scope: view ? view.scope : null, costing: view?.costing ? view.costing.form : null, keys });
}

/**
 * The saved quote's builder view arrived: while nothing is unsaved, the form takes its scope (a scope
 * a failed load left out, or one that is out of date). Changes and their tags are kept.
 */
export function reseedScope(state, scope) {
  if (!state || isDirty(state)) return state;
  const next = scopeForm(scope);
  const cur = state.form.scope;
  if ((!cur && !next) || (cur && next && SCOPE_FIELDS.every((k) => same(cur[k], next[k])))) return state;
  return { ...state, form: { ...state.form, scope: next }, saved: { ...state.saved, scope: next } };
}

/** The same for the cost inputs (LP-424): the saved view's costing.form, while nothing is unsaved. */
export function reseedCosting(state, costing) {
  if (!state || isDirty(state)) return state;
  const next = costingForm(costing);
  const cur = state.form.costing;
  if ((!cur && !next) || (cur && next && COSTING_FIELDS.every((k) => same(cur[k], next[k])))) return state;
  return { ...state, form: { ...state.form, costing: next }, saved: { ...state.saved, costing: next } };
}

export function initState(form) {
  return { form, saved: form, changes: [], tags: {} };
}

// The same value for the form's purposes: numbers by value ("1.70" = 1.7), blanks alike, the rest as text.
const norm = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  const s = String(v).trim();
  const n = Number(s.replace(/[$,\s]/g, ''));
  return s !== '' && /^-?[$\d,.\s]+$/.test(s) && Number.isFinite(n) ? String(n) : s;
};
const same = (a, b) => norm(a) === norm(b);
const clone = (x) => (x === undefined ? undefined : JSON.parse(JSON.stringify(x)));

/** Every path where two forms differ, with the value before and after (a line in or out: { line, index }, or null). */
export function diffForms(a, b) {
  const paths = []; const before = {}; const after = {};
  const put = (p, x, y) => { paths.push(p); before[p] = clone(x); after[p] = clone(y); };
  const keys = new Set([...Object.keys(a?.fields || {}), ...Object.keys(b?.fields || {})]);
  for (const k of keys) if (!same(a?.fields?.[k], b?.fields?.[k])) put(`fields.${k}`, a?.fields?.[k] ?? '', b?.fields?.[k] ?? '');
  // The scope is compared only when both forms have one (a form without one has no scope to show or
  // put back: the quote has none, or its view did not load).
  if (a?.scope && b?.scope) for (const k of SCOPE_FIELDS) if (!same(a.scope[k], b.scope[k])) put(`scope.${k}`, a.scope[k] ?? '', b.scope[k] ?? '');
  // The cost inputs likewise (LP-424).
  if (a?.costing && b?.costing) for (const k of COSTING_FIELDS) if (!same(a.costing[k], b.costing[k])) put(`costing.${k}`, a.costing[k] ?? '', b.costing[k] ?? '');
  const la = a?.lines || []; const lb = b?.lines || [];
  const ia = new Map(la.map((l, i) => [l.id, i])); const ib = new Map(lb.map((l, i) => [l.id, i]));
  la.forEach((l, i) => { if (!ib.has(l.id)) put(`line:${l.id}`, { line: l, index: i }, null); });
  lb.forEach((l, i) => {
    if (!ia.has(l.id)) { put(`line:${l.id}`, null, { line: l, index: i }); return; }
    const o = la[ia.get(l.id)];
    // A per-sf line's quantity is its area.
    for (const k of LINE_FIELDS) if (!(k === 'qty' && l.pricing === 'per_sf') && !same(o[k], l[k])) put(`line:${l.id}.${k}`, o[k], l[k]);
  });
  return { paths, before, after };
}

/**
 * Set one path on a form (a copy). "line:<id>" takes { line, index } (put back where it was, and no
 * longer left off) or null (taken out). A field of a line that is not on the form is skipped.
 */
export function setPath(form, path, value) {
  const f = { ...form, fields: { ...form.fields }, scope: form.scope ? { ...form.scope } : form.scope, costing: form.costing ? { ...form.costing } : (form.costing ?? null), lines: [...form.lines], off: [...(form.off || [])] };
  if (path.startsWith('fields.')) { f.fields[path.slice(7)] = value; return f; }
  if (path.startsWith('scope.')) { if (f.scope) f.scope[path.slice(6)] = value; return f; }
  if (path.startsWith('costing.')) { if (f.costing) f.costing[path.slice(8)] = value; return f; }
  const m = path.match(/^line:([^.]+)(?:\.(.+))?$/);
  if (!m) return f;
  const [, id, key] = m;
  const i = f.lines.findIndex((l) => l.id === id);
  if (key) {
    if (i >= 0) f.lines[i] = { ...f.lines[i], [key]: value };
    return f;
  }
  if (value && value.line) {
    // Back on the quote, where it was.
    if (i < 0) f.lines.splice(Math.min(Math.max(0, Number(value.index) || 0), f.lines.length), 0, clone(value.line));
    f.off = f.off.filter((l) => l.id !== id);
  } else if (i >= 0) {
    // Out of the quote (a line Undo takes back out is not kept).
    f.lines.splice(i, 1);
  }
  return f;
}

/**
 * The server saved a Copilot change: next is the form for the quote it saved. Records the change
 * (null when nothing on the form moved) and tags its fields. A line the Copilot dropped is kept in off.
 */
export function copilotChange(state, next, { id, label = '', lines = [], source = null, destination = null } = {}) {
  const d = diffForms(state.form, next);
  const removed = d.paths.filter((p) => /^line:[^.]+$/.test(p) && d.after[p] === null).map((p) => d.before[p].line);
  const off = [...(state.form.off || []).filter((l) => !next.lines.some((x) => x.id === l.id) && !removed.some((x) => x.id === l.id)), ...removed];
  const form = { ...next, off };
  if (!d.paths.length) return { state: { ...state, form, saved: form }, change: null };
  // The scope the change was made on (null when the form had none): when the view is lost after it, the
  // change's own paths cannot say what it did to the scope, and Undo takes the Trowel Hard rate from here.
  const change = { id, label, lines, paths: d.paths, before: d.before, after: d.after, undone: false, scope_before: clone(state.form.scope ?? null) };
  // LP-403: facts accepted from a meeting ('meeting') or the web ('web') are tagged as such; a change of
  // the destination (not a form field) keeps the one it replaced, which its Undo puts back.
  if (source) change.source = source;
  if (destination && !same(destination.before, destination.after)) Object.assign(change, { destination_before: destination.before ?? '', destination_after: destination.after ?? '' });
  const tags = { ...state.tags };
  for (const p of d.paths) tags[p] = id;
  return { state: { ...state, form, saved: form, changes: [...state.changes, change], tags }, change };
}

// A line field on a line that is no longer on the form.
const GONE = Symbol('gone');

// The builder's Trowel Hard line: its canisters and its "1 gal per N sf" follow the scope's Trowel Hard rate.
const TH_LINE = 'trowel-hard';
const movesTh = (ch) => ch.paths.some((p) => p === `line:${TH_LINE}` || p === `line:${TH_LINE}.qty` || p === `line:${TH_LINE}.description`);

/** What a path holds on a form now: a field's value, or for "line:<id>" the line ({ line }) or null. */
function valueAt(form, path) {
  if (path.startsWith('fields.')) return form.fields?.[path.slice(7)];
  if (path.startsWith('scope.')) return form.scope ? form.scope[path.slice(6)] : GONE;
  if (path.startsWith('costing.')) return form.costing ? form.costing[path.slice(8)] : GONE;
  const m = path.match(/^line:([^.]+)(?:\.(.+))?$/);
  if (!m) return GONE;
  const l = form.lines.find((x) => x.id === m[1]);
  if (m[2]) return l ? l[m[2]] : GONE;
  return l ? { line: l } : null;
}
/** Does the path still hold what the change left in it? */
function stillAfter(form, path, after) {
  const now = valueAt(form, path);
  if (now === GONE) return false;
  if (!/^line:[^.]+$/.test(path)) return same(now, after);
  if (!now || !after) return !now && !after;
  // A line the change put on the quote: every field as the change left it (a per-sf line's quantity is its area).
  return LINE_FIELDS.every((k) => (k === 'qty' && now.line.pricing === 'per_sf') || same(now.line[k], after.line?.[k]));
}

// Words for a path, in the one line that says why Undo is off.
const SCOPE_WORDS = Object.freeze({ area_sf: 'the floor area', dosage_lb_sf: 'the dry shake rate', th_sf_per_gal: 'the Trowel Hard rate', overage_pct: 'the overage' });
const LINE_WORDS = Object.freeze({ unit_price: 'price', unit_cost: 'cost', qty: 'quantity', area_sf: 'area', dosage_lb_sf: 'dosage', waste_pct: 'waste', product_id: 'product', pricing: 'pricing', type: 'type', unit: 'unit', description: 'description', optional: 'optional choice', taxable: 'tax choice' });
const LINE_NAMES = Object.freeze({ shake: 'dry shake', 'trowel-hard': 'Trowel Hard', freight: 'freight', tools: 'tools' });
function pathWords(path, ch) {
  if (path.startsWith('fields.')) return `the ${path.slice(7).replace(/_/g, ' ')}`;
  if (path.startsWith('scope.')) return SCOPE_WORDS[path.slice(6)] || 'the scope';
  if (path.startsWith('costing.')) return path === 'costing.freight_model' ? 'the freight model' : `the cost input ${path.slice(8).replace(/_/g, ' ')}`;
  const [, id, key] = path.match(/^line:([^.]+)(?:\.(.+))?$/) || [];
  const line = ch.after[`line:${id}`]?.line || ch.before[`line:${id}`]?.line;
  const name = LINE_NAMES[id] || String(line?.description || 'a line').slice(0, 40);
  return key ? `the ${name} ${LINE_WORDS[key] || key.replace(/_/g, ' ')}` : `the ${name} line`;
}

/**
 * Why this change cannot be undone now (one line for the screen), or null when it can.
 * - Changes are undone newest first: a later change was made on top of it (an area change recounts
 *   the freight an earlier change set).
 * - Never over a later edit: once any field the change set no longer holds what the change left in it
 *   (the partner typed over it, saved or not, or a later save moved it), Undo is off for that change;
 *   it would overwrite that value. Nothing is merged.
 * - A change to the scope needs the scope on the form (it did not load), and an unsaved edit of the
 *   scope is saved first (an Undo recounts nothing, so it could not carry it). So is an unsaved edit of
 *   the cost inputs (LP-424).
 */
export function undoBlock(state, id, { destination } = {}) {
  const ch = state.changes.find((c) => c.id === id);
  if (!ch) return 'This change is not on the form any more.';
  if (ch.undone) return 'Already undone.';
  const live = state.changes.filter((c) => !c.undone);
  if (live[live.length - 1].id !== id) return 'Undo the later Copilot change first.';
  // LP-403: a change that set the destination (the quote's, given by the page) is undone only while it still holds.
  if (ch.destination_after !== undefined && destination !== undefined && !same(destination, ch.destination_after)) return 'Undo is off: the destination changed after this, and Undo would overwrite it.';
  // A change that moved the Trowel Hard line puts back the rate it was made at (undoForm): the scope it
  // was made on must be known, and the form must have a scope to carry it.
  const thRate = movesTh(ch) && !ch.paths.includes('scope.th_sf_per_gal');
  if ((ch.paths.some((p) => p.startsWith('scope.')) || (thRate && ch.scope_before)) && !state.form.scope) return 'The scope is not on the form now (it did not load, or no longer applies), so this change cannot be undone.';
  if (thRate && ch.scope_before === null && state.form.scope) return 'This change was made while the scope was not on the form (the builder view did not load), so its Trowel Hard rate is not known and it cannot be undone.';
  if (ch.paths.some((p) => p.startsWith('costing.')) && !state.form.costing) return 'The cost inputs are not on the form now (they did not load), so this change cannot be undone.';
  const moved = ch.paths.find((p) => !stillAfter(state.form, p, ch.after[p]));
  if (moved) return `Undo is off: ${pathWords(moved, ch)} changed after this, and Undo would overwrite it.`;
  if (state.form.scope && state.saved?.scope && SCOPE_FIELDS.some((k) => !same(state.form.scope[k], state.saved.scope[k]))) return 'Save your scope change first, then Undo.';
  // Likewise the cost inputs (LP-424): an Undo's save would carry them, and recount the freight with them.
  if (state.form.costing && state.saved?.costing && COSTING_FIELDS.some((k) => !same(state.form.costing[k], state.saved.costing[k]))) return 'Save your cost inputs first, then Undo.';
  return null;
}

/** Can this change be undone now? (undoBlock says why not.) */
export function canUndo(state, id, opts = {}) {
  return !undoBlock(state, id, opts);
}

/** Said with the Undo of a Copilot change that turned a firm quote budgetary (keepsBudgetary). */
export const UNDO_KEEPS_BUDGETARY = 'Undo keeps the quote budgetary. Use Make firm to confirm it again.';

/**
 * Does this change's Undo keep the quote budgetary? (LP-442 r2) True for a change that turned a firm quote
 * budgetary. Only Make firm turns a quote firm (the server refuses any save that asks it, an Undo's too), so
 * its Undo puts back the quantities and prices but not what only Make firm writes: the offer type, the
 * printed scope block and the freight line's words (budgetaryKept). The partner runs Make firm again with
 * Concria's reply.
 */
export function keepsBudgetary(state, id) {
  const ch = state.changes.find((c) => c.id === id);
  return !!ch && ch.paths.includes('fields.offer_type') && norm(ch.before['fields.offer_type']) === 'firm' && norm(ch.after['fields.offer_type']) !== 'firm';
}
// A path such an Undo leaves as the change left it: the offer type, the scope block, a freight line's description.
function budgetaryKept(path, form) {
  if (path === 'fields.offer_type' || path === 'fields.scope_text') return true;
  const m = path.match(/^line:([^.]+)\.description$/);
  return !!m && form.lines.some((l) => l.id === m[1] && l.type === 'freight');
}

/**
 * The form with one Copilot change put back: its fields as they were before it (but for what an Undo that
 * keeps the quote budgetary leaves, keepsBudgetary). Nothing is marked undone.
 */
export function undoForm(state, id) {
  const ch = state.changes.find((c) => c.id === id);
  let form = state.form;
  if (!ch) return form;
  const keep = keepsBudgetary(state, id);
  // Lines back in first (their fields then apply to them), lines out last.
  const order = [...ch.paths].filter((p) => !(keep && budgetaryKept(p, state.form))).sort((a, b) => rank(a, ch) - rank(b, ch));
  for (const p of order) form = setPath(form, p, clone(ch.before[p]));
  // The Trowel Hard rate goes back with its line: the one the change recorded before it (its scope path,
  // else the scope it was made on), never one read from the line's description (LP-424 fix round 1).
  if (movesTh(ch) && !ch.paths.includes('scope.th_sf_per_gal') && ch.scope_before && form.scope) form = setPath(form, 'scope.th_sf_per_gal', ch.scope_before.th_sf_per_gal ?? '');
  return form;
}
function rank(p, ch) {
  if (!/^line:[^.]+$/.test(p)) return 1;
  return ch.before[p] ? 0 : 2;
}

/** The change's Undo was saved: it is marked undone and its fields lose their tags. */
export function markUndone(state, id) {
  const ch = state.changes.find((c) => c.id === id);
  if (!ch) return state;
  const tags = { ...state.tags };
  for (const p of ch.paths) if (tags[p] === id) delete tags[p];
  return { ...state, tags, changes: state.changes.map((c) => (c.id === id ? { ...c, undone: true } : c)) };
}

/**
 * Undo one Copilot change in the form: undoForm() then markUndone() (unchanged when undoBlock() says
 * no). The page instead saves undoForm() and marks the change undone only once that save succeeded.
 */
export function undo(state, id) {
  if (undoBlock(state, id)) return state;
  return markUndone({ ...state, form: undoForm(state, id) }, id);
}

/** The hint on the offer type's "Firm" choice on a quote that is not firm (offerOptions). */
export const FIRM_BY_MAKE_FIRM = "Concria-confirmed material and shipping. Only Make firm sets it: ask the Copilot to make it firm from Concria's reply.";

/**
 * The Quote editor's offer type choices for a quote saved with this offer type (LP-442 r2). Only Make firm
 * turns a quote firm (the server refuses any other save that asks it), so "Firm" can be picked only on a
 * quote that is firm already; on any other it is off, and its hint points to Make firm.
 */
export function offerOptions(saved) {
  const firm = saved === 'firm';
  return [
    ...(!saved ? [{ value: '', label: 'Not set', hint: 'Older quote, prints as before' }] : []),
    { value: 'budgetary', label: 'Budgetary', hint: "Material plus estimated shipping. No validity date; can't be accepted online." },
    { value: 'firm', label: 'Firm', disabled: !firm, hint: firm ? 'Concria-confirmed material and shipping. The validity runs from sending this version.' : FIRM_BY_MAKE_FIRM },
  ];
}

/** A typed or clicked edit: the path takes the value and loses its "Changed by Copilot" tag. */
export function edit(state, path, value) {
  const tags = { ...state.tags };
  delete tags[path];
  if (path.startsWith('scope.')) for (const p of Object.keys(tags)) if (p.startsWith('line:') && /\.(qty|area_sf|dosage_lb_sf|waste_pct)$/.test(p) && state.form.scope) delete tags[p];
  return { ...state, form: setPath(state.form, path, value), tags };
}

/** Edit a whole line (several of its fields at once). */
export function editLine(state, id, patch) {
  let s = state;
  for (const [k, v] of Object.entries(patch)) s = edit(s, `line:${id}.${k}`, v);
  return s;
}

/** Add a typed line, or take one off the quote (a removed line is not kept). */
export function addLine(state, line) {
  return { ...state, form: { ...state.form, lines: [...state.form.lines, formLine(line)] } };
}
export function removeLine(state, id) {
  const tags = { ...state.tags };
  for (const p of Object.keys(tags)) if (p === `line:${id}` || p.startsWith(`line:${id}.`)) delete tags[p];
  return { ...state, form: { ...state.form, lines: state.form.lines.filter((l) => l.id !== id) }, tags };
}

/** The tools line's choice: 'optional' (shown, not in the total), 'included', 'dropped' (left off), or null (no tools line). */
const isTools = (l) => l.type === 'tools' || l.id === 'tools';
export function toolsChoice(form) {
  const on = form.lines.find(isTools);
  if (on) return on.optional === true || on.optional === 'true' ? 'optional' : 'included';
  return (form.off || []).some(isTools) ? 'dropped' : null;
}
export function setToolsChoice(state, choice) {
  const on = state.form.lines.find(isTools);
  const off = (state.form.off || []).find(isTools);
  const line = on || off;
  if (!line) return state;
  const tags = { ...state.tags };
  for (const p of [`line:${line.id}`, `line:${line.id}.optional`]) delete tags[p];
  let form = state.form;
  // Left off: out of the lines, kept in off so the choice can put it back.
  if (choice === 'dropped') form = on ? { ...setPath(form, `line:${line.id}`, null), off: [...(form.off || []).filter((l) => l.id !== line.id), on] } : form;
  else {
    if (!on) form = setPath(form, `line:${line.id}`, { line: off, index: form.lines.length });
    form = setPath(form, `line:${line.id}.optional`, choice === 'optional');
  }
  return { ...state, form, tags };
}

/** After a save or a reload: the saved quote's form; changes and tags are kept, and so are lines left off. */
export function synced(state, next) {
  const off = (state.form.off || []).filter((l) => !next.lines.some((x) => x.id === l.id));
  const form = { ...next, off };
  return { ...state, form, saved: form };
}

/** Does the form differ from the saved quote? */
export function isDirty(state) {
  return diffForms(state.saved, state.form).paths.length > 0;
}

/** Is this path tagged "Changed by Copilot"? */
export function changedByCopilot(state, path) {
  return !!state.tags[path];
}

/** Where the change that tagged this path came from (LP-403): 'meeting', 'web', or null (the Copilot chat). */
export function tagSource(state, path) {
  const id = state?.tags?.[path];
  return (id && state.changes.find((c) => c.id === id)?.source) || null;
}
/** Any of a line's paths tagged (for the row). */
export function lineTagged(state, id, keys = null) {
  return Object.keys(state.tags).some((p) => (keys ? keys.some((k) => p === `line:${id}.${k}`) : p === `line:${id}` || p.startsWith(`line:${id}.`)));
}

/** The lines as the server takes them (the Quote editor's shape; blanks left out). */
export function linesForSave(lines) {
  return lines.map((l) => {
    const o = { id: l.id, type: l.type, pricing: l.pricing, description: l.description, unit_price: l.unit_price, optional: l.optional === true || l.optional === 'true', taxable: l.taxable !== false && l.taxable !== 'false' };
    if (l.product_id) o.product_id = l.product_id;
    if (l.unit_cost !== '' && l.unit_cost != null) o.unit_cost = l.unit_cost;
    if (l.pricing === 'per_sf') Object.assign(o, { area_sf: l.area_sf, dosage_lb_sf: l.dosage_lb_sf || undefined, waste_pct: l.waste_pct || undefined });
    if (l.pricing === 'per_unit') Object.assign(o, { qty: l.qty, unit: l.unit || undefined });
    if (l.pricing === 'lump' && l.unit) o.unit = l.unit;
    return o;
  });
}

/** The header fields that changed from the saved quote, as the server takes them ('' clears; required ones are never sent blank). */
export function fieldsForSave(form, saved) {
  const out = {};
  for (const [k, v] of Object.entries(form.fields)) if (String(v ?? '') !== String(saved.fields?.[k] ?? '')) out[k] = v === '' ? null : v;
  if (out.validity_days === null) delete out.validity_days;
  if (out.terms_text === null) delete out.terms_text;
  return out;
}

/** The cost inputs as the server takes them (LP-424; null when the form has none: the server keeps the quote's own). */
export function costingForSave(costing) {
  if (!costing) return null;
  return Object.fromEntries(COSTING_FIELDS.map((k) => [k, costing[k] == null ? '' : costing[k]]));
}

/** The scope as the server takes it (numbers; null without a scope). */
export function scopeForSave(scope) {
  if (!scope) return null;
  return Object.fromEntries(SCOPE_FIELDS.map((k) => [k, scope[k] === '' || scope[k] == null ? null : scope[k]]));
}

// ── The numbers on screen (the builder view) and the customer preview ──────────────────────────────

/**
 * The builder view request for the form as it is now (QuoteEditorPage.jsx): { key, body }. body is null
 * for the saved quote (GET), else the unsaved form (POST). The key is the quote's revision, the basis
 * and the form payload: a view is shown only for the key it answers (viewFor), so after any edit, basis
 * change, save or reload, or a failed request, no older totals, margins or floor stay on screen.
 */
export function builderViewRequest(state, { revision, basis, unsaved }) {
  if (!unsaved || !state) return { key: `saved:${revision}:${basis}`, body: null };
  const f = state.form;
  const body = { form: { scope: scopeForSave(f.scope), costing: costingForSave(f.costing), lines: linesForSave(f.lines), tax_mode: f.fields.tax_mode || null, tax_rate_pct: f.fields.tax_rate_pct === '' ? null : f.fields.tax_rate_pct }, basis };
  return { key: JSON.stringify({ revision, ...body }), body };
}

/** The view to show for this key: the stored one only when it answers exactly this key, else none. */
export function viewFor(stored, key) {
  return stored && key != null && stored.key === key ? stored.view : null;
}

/**
 * Which lines the scope sets and why it sets none (the view's layout, not its numbers): from the stored
 * view, even while its numbers are invalid, but only while the quote is still at that view's revision.
 */
export function viewLayout(stored, revision) {
  return stored && stored.revision != null && stored.revision === revision ? stored.view : null;
}

/**
 * The customer preview's document (CustomerPreview.jsx): { key, html, error } for one quote revision.
 * previewStart: a new revision starts with no html (never the prior revision's); previewDone: the
 * answer for that revision (an answer for an older one is dropped; an error leaves no html);
 * previewShown: what is on screen for the current revision (nothing from another one).
 */
export const previewKey = (quoteId, revision) => `${quoteId}:${revision}`;
export function previewStart(quoteId, revision) {
  return { key: previewKey(quoteId, revision), html: null, error: '' };
}
export function previewDone(doc, key, { html = null, error = '' } = {}) {
  if (!doc || doc.key !== key) return doc;
  return error ? { key, html: null, error } : { key, html, error: '' };
}
export function previewShown(doc, quoteId, revision) {
  return doc && doc.key === previewKey(quoteId, revision) ? doc : previewStart(quoteId, revision);
}

// ── Undo: the save and the reload are separate ────────────────────────────────────────────────────

/** The Undo was saved: the form and the saved quote are the form with the change put back, and the change is undone. */
export function undoSaved(state, id) {
  const form = undoForm(state, id);
  return markUndone({ ...state, form, saved: form }, id);
}

/**
 * The page's Undo of one Copilot change (QuoteEditorPage.jsx undoChange), its I/O passed in.
 * save(form): saves the form with the change put back (throws when refused; resolves to the server's
 * answer). reload(): reloads the quote, resolving to an error message when it failed (else null). A
 * failed reload is tried again `retries` more times, after wait().
 * Returns { saved, reloaded, state, error, result }:
 * - the save failed: saved false, state as it was (the change is not undone), error = the save's;
 * - saved and reloaded: the page's reload already holds the saved quote; the change is marked undone on it;
 * - saved, the reload failed: the change IS undone (state = undoSaved), error = the reload's, and result
 *   = the save's answer (the saved quote and its new revision, for the page to show nothing older).
 */
export async function runUndo(state, id, { save, reload, retries = 1, wait = async () => {} }) {
  const why = undoBlock(state, id);
  if (why) return { saved: false, reloaded: false, state, error: why };
  let result;
  try {
    result = await save(undoForm(state, id));
  } catch (e) {
    return { saved: false, reloaded: false, state, error: e?.message || String(e), result: null };
  }
  let error = await reload();
  for (let n = 0; error && n < retries; n += 1) {
    await wait();
    error = await reload();
  }
  return error ? { saved: true, reloaded: false, state: undoSaved(state, id), error, result: result ?? null }
    : { saved: true, reloaded: true, state: null, error: null, result: result ?? null };
}
